/**
 * Settings → Public Holidays (MOR-830)
 *
 * Purpose: visibility and annual upkeep of the shared business-day calendar
 * that statutory day-counts (e.g. the s588FL 20-business-day limb) run on.
 * Shows every weekday public holiday per state for a chosen year, and lets
 * an org admin confirm the two proclamation-based holidays (VIC Grand Final
 * Friday, WA King's Birthday) once the gazetted dates are published.
 * Estimated dates are used for calculations in the meantime, with boundary
 * results flagged for manual verification rather than concluded.
 *
 * - Convex: settings.publicHolidays.listYear,
 *           settings.publicHolidays.setProclaimedDate
 */
import { createFileRoute } from "@tanstack/react-router";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { useActiveOrg } from "@/utils/org-context";
import { Button, Label } from "@/ui/primitives";
import { Badge } from "@/ui/primitives/badge";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives";
import { useState } from "react";
import { toast } from "sonner";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

const YEARS = Array.from({ length: 2036 - 2010 + 1 }, (_, i) => 2010 + i);
const JURISDICTIONS = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/public-holidays",
)({
  component: PublicHolidaysSettings,
  beforeLoad: () => ({
    title: "Public Holidays",
    headerTitle: "Public Holidays",
    headerDescription:
      "The state-by-state calendar behind statutory business-day calculations. Confirm each year's proclaimed dates when gazetted.",
  }),
});

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function PublicHolidaysSettings() {
  const { orgId, activeOrg } = useActiveOrg();
  const canEdit = activeOrg?.role === "owner" || activeOrg?.role === "admin";
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [jurisdiction, setJurisdiction] = useState("NSW");

  const { data } = useQuery(
    convexQuery(api.settings.publicHolidays.listYear, { year }),
  );

  const setDate = useMutation({
    mutationFn: useConvexMutation(
      api.settings.publicHolidays.setProclaimedDate,
    ),
    onSuccess: () => toast.success("Holiday date confirmed"),
    onError: (e) => toast.error(String(e)),
  });

  if (!orgId) {
    return (
      <div className="text-sm text-muted-foreground">
        Select an organisation to view public holidays.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-5">
        <div className="flex items-center gap-3">
          <Label>Year</Label>
          <Select
            value={String(year)}
            onValueChange={(v) => setYear(Number(v))}
          >
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          <Label>State / territory</Label>
          <Select value={jurisdiction} onValueChange={setJurisdiction}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {JURISDICTIONS.map((j) => (
                <SelectItem key={j} value={j}>
                  {j}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className={GROUP_CARD_CLASS}>
        <div className="border-b border-border/50 p-4">
          <h4 className="text-sm font-semibold">Proclaimed dates for {year}</h4>
          <p className="text-xs text-muted-foreground">
            The only holidays that cannot be derived from rules. Estimated dates
            follow the usual pattern and are used in calculations, but boundary
            results stay flagged for manual verification until the gazetted date
            is confirmed here.
          </p>
        </div>
        <div className="divide-y divide-border/40">
          {(data?.proclaimed ?? []).map((p) => (
            <ProclaimedRow
              key={`${p.jurisdiction}:${p.label}`}
              orgId={orgId as Id<"organizations">}
              entry={p}
              canEdit={canEdit}
              onConfirm={(date) =>
                setDate.mutate({
                  orgId: orgId as Id<"organizations">,
                  year: p.year,
                  jurisdiction: p.jurisdiction,
                  label: p.label,
                  date,
                })
              }
            />
          ))}
          {data && data.proclaimed.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">
              No proclamation-based holidays recorded for {year}.
            </p>
          )}
        </div>
      </div>

      <div className={GROUP_CARD_CLASS}>
        <div className="border-b border-border/50 p-4">
          <h4 className="text-sm font-semibold">
            Public holidays in {jurisdiction}, {year}
          </h4>
          <p className="text-xs text-muted-foreground">
            Every weekday holiday the business-day calculations exclude for this
            state, with the rule that produces each date. Weekend-falling
            holidays appear as their substitute weekday, since only weekdays
            affect a business-day count. Regional-only days (NT show days, the
            Brisbane Ekka) and part-day holidays are excluded by design.
          </p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/40 text-left text-xs text-muted-foreground">
              <th className="p-3 font-medium">Date</th>
              <th className="p-3 font-medium">Holiday</th>
              <th className="p-3 font-medium">Rule</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/30">
            {(
              data?.jurisdictions.find((j) => j.jurisdiction === jurisdiction)
                ?.holidays ?? []
            ).map((h) => (
              <tr key={h.date}>
                <td className="whitespace-nowrap p-3 font-medium">
                  {formatDate(h.date)}
                </td>
                <td className="whitespace-nowrap p-3">
                  <span className="flex items-center gap-2">
                    {h.name}
                    {h.source === "estimated" && (
                      <Badge color="amber" variant="soft">
                        Estimated
                      </Badge>
                    )}
                    {h.source === "confirmed" && (
                      <Badge color="emerald" variant="soft">
                        Proclaimed
                      </Badge>
                    )}
                  </span>
                </td>
                <td className="p-3 text-xs text-muted-foreground">{h.rule}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProclaimedRow({
  entry,
  canEdit,
  onConfirm,
}: {
  orgId: Id<"organizations">;
  entry: {
    jurisdiction: string;
    label: string;
    date: string;
    status: "confirmed" | "estimated";
    adjusted: boolean;
  };
  canEdit: boolean;
  onConfirm: (date: string) => void;
}) {
  const [draft, setDraft] = useState(entry.date);

  return (
    <div className="flex flex-wrap items-center gap-3 p-4">
      <div className="min-w-48">
        <p className="text-sm font-medium">{entry.label}</p>
        <p className="text-xs text-muted-foreground">
          {entry.jurisdiction === "ALL"
            ? "All jurisdictions"
            : entry.jurisdiction}
        </p>
      </div>
      <Badge
        color={entry.status === "confirmed" ? "emerald" : "amber"}
        variant="soft"
      >
        {entry.status === "confirmed"
          ? entry.adjusted
            ? "Confirmed (adjusted)"
            : "Confirmed"
          : "Estimated"}
      </Badge>
      <div className="ml-auto flex items-center gap-2">
        <DatePicker
          value={draft}
          onChange={(next) => setDraft(next ?? entry.date)}
          disabled={!canEdit}
        />
        {canEdit && (
          <Button
            size="sm"
            variant={entry.status === "confirmed" ? "outline" : "default"}
            onClick={() => onConfirm(draft)}
          >
            {entry.status === "confirmed" ? "Update" : "Confirm"}
          </Button>
        )}
      </div>
    </div>
  );
}
